import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { ComplianceTable } from "../components/acms/Shared";
import { EquipmentCard as Card, Stat } from "../components/acms/EquipmentCard";
import { DamperIcon } from "../components/acms/EquipmentIcons";
import { Fan, Wind } from "lucide-react";

export default function VentilationPage() {
  const { selectedSite, sites } = useSiteContext();
  const [loaded, setLoaded] = useState(false);
  const [fans, setFans] = useState([]);
  const [ventilationValves, setVentilationValves] = useState([]);
  const [liveValues, setLiveValues] = useState({});

  useEffect(() => {
    if (!selectedSite) return;
    setLoaded(false);
    // The old `/telemetry/{site}` simulator fetch that used to gate this page was never read
    // anywhere — it only held the page on "Loading…" until it answered. Gate on the real data.
    const load = () => {
      Promise.all([
        api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "fan" } }).then((r) => setFans(r.data || [])).catch(() => {}),
        api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "valve", system: "ventilation" } }).then((r) => setVentilationValves(r.data || [])).catch(() => {}),
        api.get(`/assets/live-values`, { params: { site_id: selectedSite } }).then((r) => setLiveValues(r.data || {})).catch(() => {}),
      ]).finally(() => setLoaded(true));
    };
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [selectedSite]);

  if (!loaded) return <div className="text-gray-500">Loading ventilation telemetry…</div>;
  const site = sites.find((s) => s.site_id === selectedSite);

  const complianceRows = fans.map((fan) => {
    const live = liveValues[fan.asset_id];
    const running = live ? Number(live.value) === 1 : null;
    return {
      parameter: fan.name,
      current: live ? (running ? "Running" : "Stopped") : "—",
      required: "Operational",
      ok: live ? running : fan.status === "active",
    };
  });

  return (
    <div className="space-y-3" data-testid="ventilation-page">
      {/* Header — one compact row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <div className="w-9 h-9 rounded-lg bg-cyan-50 border border-cyan-200 text-cyan-600 flex items-center justify-center shrink-0">
            <Wind className="w-5 h-5" strokeWidth={2} />
          </div>
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">Ventilation System</h1>
          <span className="text-[11px] text-gray-500 truncate">
            Dashboard → {site?.name} · Pressurization · smoke exhaust · life-safety · auto-refresh 5s
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <a href="/registry/fans" className="text-orange-600 hover:underline">Fan Registry →</a>
          <a href="/registry/valves" className="text-orange-600 hover:underline">Valve Registry →</a>
        </div>
      </div>

      {!fans.length && !ventilationValves.length ? (
        <div className="glass rounded-lg p-8 text-center text-gray-500 text-sm">
          No ventilation fans registered yet. <a href="/registry/fans" className="text-orange-600 hover:text-orange-700 font-semibold">Add fans in Registry</a>
        </div>
      ) : (
        // Fans and dampers share one dense grid so the whole system fits on screen; each card's
        // small orange label names its group, and the icon badge's color shows its state (the
        // fan icon also spins while the fan is running).
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))" }}>
          {fans.map((fan) => {
            const live = liveValues[fan.asset_id];
            const running = live ? Number(live.value) === 1 : null;
            return (
              <Card
                key={fan.asset_id}
                title={fan.name}
                group={fan.meta?.fan_type ? `Fan · ${fan.meta.fan_type}` : "Ventilation Fan"}
                icon={Fan}
                iconClassName={running ? "animate-[spin_3s_linear_infinite]" : ""}
                tone={running === null ? "idle" : running ? "good" : "idle"}
                pulse={running === true}
                live={live}
                testid={`card-fan-${fan.asset_id}`}
                footer={live && (
                  <div className="grid grid-cols-2 gap-1 mt-2">
                    <Stat label="Type">{fan.meta?.fan_type || "—"}</Stat>
                    <Stat label="Location">{fan.location_label || fan.location || "—"}</Stat>
                  </div>
                )}
              >
                {live ? (
                  <div className="text-lg font-extrabold uppercase tracking-wider leading-tight">{running ? "Running" : "Stopped"}</div>
                ) : (
                  <>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold py-0.5">
                      {fan.gateway_id ? "Awaiting data" : "Not wired to live data"}
                    </div>
                    <div className="text-[10px] text-gray-500">Static: <span className="text-gray-700 font-semibold">{fan.status || "active"}</span></div>
                  </>
                )}
              </Card>
            );
          })}

          {ventilationValves.map((v) => {
            const live = liveValues[v.asset_id];
            const open = live ? Number(live.value) === 1 : null;
            return (
              <Card
                key={v.asset_id}
                title={v.name}
                group="Valve / Damper"
                icon={DamperIcon}
                tone={open === null ? "idle" : open ? "good" : "idle"}
                live={live}
                testid={`card-vent-valve-${v.asset_id}`}
              >
                <div className="text-lg font-extrabold uppercase tracking-wider leading-tight truncate">
                  {open === null ? (v.gateway_id ? "Awaiting data" : "Not linked") : open ? "Open" : "Closed"}
                </div>
                <div className="text-[10px] text-gray-500 truncate">{v.location_label || "—"} · {v.meta?.type || "—"}</div>
              </Card>
            );
          })}
        </div>
      )}

      {/* GR compliance — mandatory, so always shown inline below the cards (not behind a modal) */}
      {fans.length > 0 && (
        <ComplianceTable title="Section E — Ventilation & Life-Safety" rows={complianceRows} />
      )}
    </div>
  );
}
